import { create } from '@bufbuild/protobuf'
import { timestampDate } from '@bufbuild/protobuf/wkt'
import type { Timestamp } from '@bufbuild/protobuf/wkt'
import { Code, ConnectError } from '@connectrpc/connect'
import { UpdatePreviousApprovalNoteRequestSchema } from '@ebalance/proto/bffuser/v1/workflow_case_service_pb'
import { useTranslation } from 'react-i18next'

import { Box, Text, VStack } from '@enechain/ecloud-designsystem'

import { useSuccessToast } from '~/hooks/useSuccessToast'
import { formatDateTimeFull } from '~/models/dateTime'
import { MemoDialog } from '~/pages/WorkflowCaseDetail/components/MemoDialog'
import { useUpdatePreviousApprovalNote } from '~/pages/WorkflowCaseDetail/hooks/useUpdatePreviousApprovalNote'

type Props = {
  isOpen: boolean
  caseId: string
  bigStepDisplayOrder: number
  subStepDisplayOrder: number
  stageOrder: number
  approverMemberId: string
  cancelledAt: Timestamp
  initialNote: string
  approverName: string
  approvedAt: Timestamp | undefined
  onClose: () => void
}

/**
 * 「前回承認時の申し送り」(= 差戻し後に保持される承認メモ) の編集ダイアログ
 * (workflow-fix-approval-ux Design Note Decision #3、spec AC-WFW-007c/d)。
 * identity は (stageOrder, approverMemberId, cancelledAt) の複合。
 * ApprovalMemoDialog (active 承認メモ用) と役割が同じだが、対象 RPC / identity
 * が異なるため独立 component にしている。
 */
export const PreviousApprovalMemoDialog = ({
  isOpen,
  caseId,
  bigStepDisplayOrder,
  subStepDisplayOrder,
  stageOrder,
  approverMemberId,
  cancelledAt,
  initialNote,
  approverName,
  approvedAt,
  onClose,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const showSuccessToast = useSuccessToast()
  const { mutateAsync, isPending } = useUpdatePreviousApprovalNote(caseId)

  const handleSave = async (memo: string): Promise<void> => {
    await mutateAsync(
      create(UpdatePreviousApprovalNoteRequestSchema, {
        workflowCaseId: caseId,
        bigStepDisplayOrder,
        subStepDisplayOrder,
        stageOrder,
        approverMemberId,
        cancelledAt,
        note: memo === '' ? undefined : memo,
      }),
    )
    // 2026-08-28 デザイナー指摘「削除・追加・編集の成功時は全画面共通で toast」。
    showSuccessToast({
      title: t('page:workflowCaseDetail.approvalToast.memoSaveToast'),
    })
  }

  const mapError = (err: unknown): string | undefined => {
    if (err instanceof ConnectError) {
      if (err.code === Code.Aborted) {
        return t('page:workflowCaseDetail.error.aborted')
      }
      if (err.code === Code.FailedPrecondition) {
        return t('page:workflowCaseDetail.error.failedPrecondition')
      }
    }
    return undefined
  }

  return (
    <MemoDialog
      context={
        <VStack align="stretch" gap={1}>
          <Text textStyle="label" color="Text.highEmphasis">
            {t('page:workflowCaseDetail.approval.memoDialogApproverLabel')}
          </Text>
          <Box>
            <Text color="Text.highEmphasis" fontSize="sm">
              {approverName}
            </Text>
          </Box>
          <Text textStyle="label" color="Text.highEmphasis">
            {t('page:workflowCaseDetail.approval.memoDialogAtLabel')}
          </Text>
          <Text color="Text.midEmphasis" fontSize="sm">
            {approvedAt !== undefined
              ? formatDateTimeFull(timestampDate(approvedAt))
              : '-'}
          </Text>
        </VStack>
      }
      initialMemo={initialNote}
      isOpen={isOpen}
      isPending={isPending}
      mapError={mapError}
      onClose={onClose}
      onSave={handleSave}
      placeholder={t('page:workflowCaseDetail.approval.memoDialogPlaceholder')}
      saveLabel={t('page:workflowCaseDetail.approval.memoDialogSave')}
      textareaLabel={t(
        'page:workflowCaseDetail.approval.memoDialogTextareaLabel',
      )}
      title={t('page:workflowCaseDetail.approval.memoDialogTitle')}
    />
  )
}
